/**
 * In-app history-index voor de `BackButton` (spec consistente-terugknop).
 *
 * De `BackButton` moet weten of de vorige history-entry nog binnen de app ligt
 * (dan `router.back()`) of niet (deep-link / nieuw tabblad / koude PWA-start:
 * dan de fallback). Eerder werd daarvoor `history.state.idx` gelezen, maar die
 * schrijft alleen de oude Pages Router; de App Router (Next 14) zet 'm nooit.
 * Daardoor was "Terug" altijd de fallback: wie vanuit de Gesprekken-tab van een
 * medewerker een gespreksverslag opende, landde op het verslagen-overzicht.
 *
 * Daarom stempelen we zelf een volgnummer op elke entry: `pushState` krijgt
 * het nummer van de huidige entry + 1, `replaceState` neemt het huidige nummer
 * over (ook wanneer Next of een eigen `replaceState(null, ...)` de state
 * vervangt). Terug/vooruit en reload herstellen de state van de browser, dus
 * het nummer blijft kloppen. Een entry zonder nummer (eerste entry, of een
 * entry van buiten de app) telt als 0.
 *
 * Pure helper zonder Next- of DOM-afhankelijkheid, zodat hij met een nep-
 * `History` testbaar is.
 */

export const HISTORY_INDEX_KEY = '__streepIdx'

type HistoryLike = Pick<History, 'state' | 'pushState' | 'replaceState'>

export function readHistoryIndex(state: unknown): number {
  if (state == null || typeof state !== 'object') return 0
  const idx = (state as Record<string, unknown>)[HISTORY_INDEX_KEY]
  return typeof idx === 'number' && Number.isFinite(idx) && idx > 0 ? idx : 0
}

function withIndex(data: unknown, idx: number): Record<string, unknown> {
  const base = data != null && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  return { ...base, [HISTORY_INDEX_KEY]: idx }
}

/**
 * Wikkelt `pushState`/`replaceState` van `history` zodat elke entry een
 * volgnummer draagt. Geeft een functie terug die de originele methodes
 * terugzet (alleen als niemand er inmiddels overheen heeft gewikkeld).
 */
export function installHistoryIndex(history: HistoryLike): () => void {
  const originalPush = history.pushState
  const originalReplace = history.replaceState

  const pushState: History['pushState'] = function (data, unused, url) {
    const next = readHistoryIndex(history.state) + 1
    return originalPush.call(history, withIndex(data, next), unused, url)
  }
  const replaceState: History['replaceState'] = function (data, unused, url) {
    const current = readHistoryIndex(history.state)
    return originalReplace.call(history, withIndex(data, current), unused, url)
  }

  history.pushState = pushState
  history.replaceState = replaceState

  return () => {
    if (history.pushState === pushState) history.pushState = originalPush
    if (history.replaceState === replaceState) history.replaceState = originalReplace
  }
}
